import { Text, View } from "@react-pdf/renderer";
import { getTenantStore } from "../../../../stores/tenantStore";
import { buildReportingHubFilePreviewUrl } from "../../../../utils/reports";
import { styles } from "../../ReactPdfStyles";
import NotProjectRelatedSection from "../NotProjectRelatedSection";
import ReportingApprovalRecordSection from "../ReportingApprovalRecordSection";
import ReportingFileList from "../ReportingFileList";
import ReportingProjectDetailSection from "../ReportingProjectDetailSection";
import ReportPdfLayout from "../ReportPdfLayout";
import { formatDate, formatResponsiblePerson } from "../utils";

interface OfiPDFTemplateProps {
	data: Record<string, any>;
}

export default function OfiPDFTemplate({ data }: OfiPDFTemplateProps) {
	return (
		<ReportPdfLayout reportTitle="OPPORTUNITY FOR IMPROVEMENTS" data={data}>
			{data?.is_project_related ? (
				<ReportingProjectDetailSection data={data} />
			) : (
				<NotProjectRelatedSection data={data} />
			)}
			<DetailSection data={data} />
			<ReportingApprovalRecordSection data={data} />
			<FollowUpActionsSection
				follow_up_actions={data.follow_up_actions ?? []}
			/>
		</ReportPdfLayout>
	);
}

function DetailSection({ data }: { data: Record<string, any> }) {
	const description = data?.description ?? "N/A";
	const tenantList = getTenantStore().getTenantList();
	const tenant = tenantList.find(
		(tenant: any) => tenant.value === data.operator
	);
	const containerId = tenant?.container_id;

	const renderFiles = () => {
		const files = data.additional_files;
		if (!files.length) return <Text>—</Text>;
		const origin = window.location.origin;
		const previewUrl = `${origin}/reporting/ofi-stream/${data._id}/preview`;
		return (
			<ReportingFileList
				files={files}
				buildUrl={(fileObj: any) =>
					containerId
						? buildReportingHubFilePreviewUrl(previewUrl, fileObj, containerId)
						: null
				}
			/>
		);
	};

	return (
		<View style={[styles.section, styles.tableFontSize]}>
			<Text style={styles.tableHeader}>OFI detail</Text>
			<View style={styles.row}>
				<Text style={styles.labelCell}>Description</Text>
				<Text style={[styles.valueCell, styles.borderRight]}>
					{description}
				</Text>
			</View>

			<View style={styles.row}>
				<Text style={styles.labelCell}>File upload(s)</Text>
				<View style={[styles.valueCell, styles.borderRight]}>
					{renderFiles()}
				</View>
			</View>
		</View>
	);
}

function FollowUpActionsSection({
	follow_up_actions,
}: {
	follow_up_actions: any[];
}) {
	const renderCompletedBy = (action: any) => {
		const responsibleDisplay = formatResponsiblePerson(
			action?.responsible,
			action?.responsible_name
		);
		const completed = action.completed_on
			? `${formatDate(action.completed_on as any)}`
			: null;
		return (
			<View style={styles.column}>
				{responsibleDisplay && (
					<View style={{ marginBottom: 8 }}>
						<Text
							style={{
								fontWeight: "bold",
							}}
							wrap={false}
						>
							Person Responsible:
						</Text>

						<Text>{responsibleDisplay}</Text>
					</View>
				)}
				{completed ? (
					<View style={{ marginBottom: 8 }}>
						<Text style={{ fontWeight: "bold" }}>Completed by:</Text>
						<Text>{`${responsibleDisplay} (${completed})`}</Text>
					</View>
				) : (
					<Text style={{ fontStyle: "italic" }}>Pending completion</Text>
				)}

				{/* Spacing */}
				{action.closing_comment && (
					<>
						<View style={{ height: 6 }} />
						{/* Closing comment section */}
						<Text style={{ fontWeight: "bold" }}>Closing comment:</Text>
						<Text>{action.closing_comment}</Text>
					</>
				)}
			</View>
		);
	};
	return (
		<View break style={[styles.section, styles.tableFontSize]}>
			<Text style={[styles.tableHeader, { marginBottom: 15 }]}>Actions</Text>
			<View style={styles.row}>
				<Text
					style={[
						styles.borderTop,
						styles.borderLeft,
						{
							flex: 1,
							fontWeight: "bold",
							backgroundColor: "#F2F2F2",
							padding: 8,
						},
					]}
				>
					Action no.
				</Text>
				<Text
					style={[
						styles.borderTop,
						styles.borderLeft,
						{
							flex: 1,
							fontWeight: "bold",
							backgroundColor: "#F2F2F2",
							padding: 8,
						},
					]}
				>
					Description
				</Text>
				<Text
					style={[
						styles.borderTop,
						styles.borderLeft,
						styles.borderRight,
						{
							flex: 1,
							fontWeight: "bold",
							backgroundColor: "#F2F2F2",
							padding: 8,
						},
					]}
				>
					Remarks
				</Text>
			</View>
			<View>
				{Array.isArray(follow_up_actions) && follow_up_actions.length > 0 ? (
					follow_up_actions.map((action: any) => (
						<View key={action.action_number} style={styles.row}>
							<Text style={[styles.valueCell, styles.borderLeft]}>
								{action.action_number ?? "N/A"}
							</Text>
							<Text style={[styles.valueCell, styles.borderLeft]}>
								{action.description ?? "N/A"}
							</Text>
							<View
								style={[
									styles.valueCell,
									styles.borderLeft,
									styles.borderRight,
								]}
							>
								{renderCompletedBy(action)}
							</View>
						</View>
					))
				) : (
					<View style={styles.row}>
						<Text
							style={[
								styles.valueCell,
								styles.borderLeft,
								styles.borderRight,
								{ textAlign: "center" },
							]}
						>
							No follow-up actions available
						</Text>
					</View>
				)}
			</View>
		</View>
	);
}
